import Box from "@memorang/ui/components/Box";
import { CMENameConfirmationDialog } from "@memorang/ui/components/Cme/CMENameConfirmationDialog";
import { CMETypeSelector } from "@memorang/ui/components/Cme/CMETypeSelector";
import { DateRangeInput } from "@memorang/ui/components/Cme/DateRangeInput";
import { ExportActionBar } from "@memorang/ui/components/Cme/ExportActionBar";
import { ExportMethodSelector } from "@memorang/ui/components/Cme/ExportMethodSelector";
import Container from "@memorang/ui/components/Container";
import { useRouter } from "expo-router";
import { useState } from "react";
import { ScrollView } from "react-native";
import { CME_TYPES, getExportMethods, mockCmeSummary } from "../constants";
import { useCmeCertificateExport } from "../hooks/useCmeCertificateExport";
import {
	type CmeExportMethod,
	type CmeType,
	isCmeExportMethod,
	isCmeType,
} from "../types";

export const CMEExportContainer = () => {
	const router = useRouter();
	const [selectedDateRange, setSelectedDateRange] = useState({
		from: "",
		to: "",
	});
	const [selectedExportMethod, setSelectedExportMethod] =
		useState<CmeExportMethod>("pdf");
	const [selectedCMEType, setSelectedCMEType] = useState<CmeType>("physician");
	const [showConfirmNameDialog, setShowConfirmNameDialog] = useState(false);

	const {
		firstName,
		setFirstName,
		lastName,
		setLastName,
		resetNamesToInitial,
		handleExport,
		toEmail,
		isPending,
	} = useCmeCertificateExport();

	const totalCreditsEarned =
		selectedDateRange.from && selectedDateRange.to
			? mockCmeSummary.earnedCredits
			: 0;

	const handleDismissConfirmNameDialog = () => {
		resetNamesToInitial();
		setShowConfirmNameDialog(false);
	};

	const handleConfirmName = () => {
		setShowConfirmNameDialog(false);
		handleExport({
			fromDate: selectedDateRange.from,
			toDate: selectedDateRange.to,
			selectedMethod: selectedExportMethod,
			cmeType: selectedCMEType,
			totalCreditsEarned,
		});
	};

	const handleNavigateToActivityLog = () => {
		router.push({
			pathname: "/cme/log",
			params:
				selectedDateRange.from && selectedDateRange.to
					? {
							from: selectedDateRange.from,
							to: selectedDateRange.to,
						}
					: undefined,
		});
	};

	return (
		<Container containerStyle={{ flex: 1 }}>
			<ScrollView contentContainerStyle={{ flexGrow: 1 }}>
				<Box gap={16} paddingVertical={16} flex={1}>
					<DateRangeInput
						value={selectedDateRange}
						onChange={setSelectedDateRange}
					/>
					<CMETypeSelector
						types={CME_TYPES}
						selectedType={selectedCMEType}
						onSelect={(id) => {
							if (isCmeType(id)) {
								setSelectedCMEType(id);
							}
						}}
					/>
					<ExportMethodSelector
						methods={getExportMethods(toEmail)}
						selectedMethod={selectedExportMethod}
						onSelect={(id) => {
							if (isCmeExportMethod(id)) {
								setSelectedExportMethod(id);
							}
						}}
					/>
				</Box>
			</ScrollView>

			{/* totalCreditsEarned is mocked (see ../constants) — export must stay
			disabled so a real certificate is never issued from a fabricated
			credit total. Re-enable once earned credits come from a real source. */}
			<ExportActionBar
				selectedMethod={selectedExportMethod}
				totalCreditsEarned={totalCreditsEarned}
				disabled
				onNavigateToActivityLog={handleNavigateToActivityLog}
				onExport={() => setShowConfirmNameDialog(true)}
				isLoading={isPending}
				toEmail={toEmail}
			/>
			<CMENameConfirmationDialog
				visible={showConfirmNameDialog}
				firstName={firstName}
				lastName={lastName}
				onChangeFirstName={setFirstName}
				onChangeLastName={setLastName}
				onDismiss={handleDismissConfirmNameDialog}
				onConfirm={handleConfirmName}
				isLoading={isPending}
			/>
		</Container>
	);
};
